import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { beautifyEnum } from 'src/utils/utility';
import StatusChip from 'src/presentations/components/organisms/dashboard/StatusChip';
import { ITableInvoiceChargeDTO } from './types';

export const TableMain = styled(Box)({
  '.MuiTableCell-head:first-of-type': {
    width: '40px',
  },
});

export interface ITableInvoiceChargeProps {
  data: ITableInvoiceChargeDTO[];
}

export const TableInvoiceCharge = ({ data }: ITableInvoiceChargeProps) => {
  const hasDiscountAmount = data.some((row) => row.discountAmount != null && row.discountAmount !== '');
  const isDoubleCharge = data.some((row) => row.isCurrentInvoice !== undefined);
  const hasDeviationType = isDoubleCharge && data.some((row) => row.deviationType != null && row.deviationType !== '');
  const hasDeviationWorkflow = data.some((row) => row.currentDeviationWorkflow != null && row.currentDeviationWorkflow !== '');

  const columns: MRT_ColumnDef<ITableInvoiceChargeDTO>[] = [
    {
      accessorKey: 'chargeName',
      header: 'Charge Name',
      enableEditing: false,
      accessorFn: (row) => (
        <div>
          <div className='text-14 font-medium text-neutral-80'>{row?.chargeName}</div>
          {row?.isCurrentInvoice !== undefined && (
            <div className='flex items-center gap-6 mt-2'>
              <span className='text-12 font-medium' style={{ color: '#28a745' }}>
                {row?.isCurrentInvoice ? 'Current Invoice' : `Inv No. ${row.invoiceNumber}`}
              </span>
              {row?.deviationStatus && <StatusChip status={row.deviationStatus} size='small' />}
            </div>
          )}
        </div>
      ),
    },
    ...(hasDeviationType
      ? [
          {
            accessorKey: 'deviationType',
            header: 'Deviation Type',
            enableEditing: false,
            accessorFn: (row: ITableInvoiceChargeDTO) => <span className='capitalize'>{beautifyEnum(row?.deviationType)}</span>,
            muiTableBodyCellProps: { sx: { borderBottom: '1px solid #DFDFDF!important' } },
          } as MRT_ColumnDef<ITableInvoiceChargeDTO>,
        ]
      : []),
    {
      accessorKey: 'unitPrice',
      header: 'Unit Price',
      enableEditing: false,
      accessorFn: (row) => <span className='capitalize'>{row?.unitPrice}</span>,
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      enableEditing: false,
      accessorFn: (row) => <span className='capitalize'>{row?.quantity}</span>,
    },
    ...(hasDiscountAmount
      ? [
          {
            accessorKey: 'discountAmount',
            header: 'Discount Amount',
            enableEditing: false,
            accessorFn: (row: ITableInvoiceChargeDTO) => <span className='capitalize'>{row?.discountAmount}</span>,
          } as MRT_ColumnDef<ITableInvoiceChargeDTO>,
        ]
      : []),
    ...(hasDeviationWorkflow
      ? [
          {
            accessorKey: 'currentDeviationWorkflow',
            header: 'Current Workflow',
            enableEditing: false,
            accessorFn: (row: ITableInvoiceChargeDTO) => <span className='text-14 text-neutral-70'>{beautifyEnum(row?.currentDeviationWorkflow) || '—'}</span>,
            muiTableBodyCellProps: { sx: { borderBottom: '1px solid #DFDFDF!important' } },
          } as MRT_ColumnDef<ITableInvoiceChargeDTO>,
        ]
      : []),
  ];

  const table = useMaterialReactTable({
    data,
    enableRowNumbers: false,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    columns,
  });
  return <CustomTable table={table} />;
};
